import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useIsMobile } from '../hooks/useIsMobile';
import './Navigation.css';

const Navigation: React.FC = () => {
  const { currentUser, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isAcceptingRegistrations, setIsAcceptingRegistrations] = useState<boolean | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setCheckingStatus(false);
      return;
    }

    const checkRegistrationStatus = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'camp');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          setIsAcceptingRegistrations(settingsDoc.data()?.acceptingRegistrations ?? false);
        } else {
          setIsAcceptingRegistrations(false);
        }
      } catch (error) {
        console.error('Error checking registration status:', error);
        setIsAcceptingRegistrations(false);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkRegistrationStatus();
  }, [currentUser]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to logout:', err);
    }
  };

  const go = (path: string, state?: object) => {
    setMenuOpen(false);
    navigate(path, state ? { state } : undefined);
  };

  // Only hide navigation on login page
  if (location.pathname === '/login') {
    return null;
  }

  // Show loading state while checking registration status
  if (checkingStatus) {
    return (
      <nav className="navigation-bar">
        <div className="nav-brand">
          <h1>Berwick Registration</h1>
        </div>
        <div className="nav-buttons">
          <div className="user-email">
            {currentUser?.email}
          </div>
        </div>
      </nav>
    );
  }

  const showRegistrationLink = isAdmin || isAcceptingRegistrations;

  const navButtons = (
    <>
      <div className="user-email">
        {currentUser?.email}
      </div>
      {showRegistrationLink && (
        <button 
          onClick={() => go('/register')} 
          className="nav-button rainbow-orange"
        >
          Back to Registration
        </button>
      )}
      {isAdmin && (
        <>
          <button 
            onClick={() => go('/admin/assignments')} 
            className="nav-button rainbow-green"
          >
            Assignment
          </button>
          <button 
            onClick={() =>
              go('/admin/reports', {
                resetToReportsList: true,
                requestedAt: Date.now()
              })
            } 
            className="nav-button rainbow-blue"
          >
            Reports
          </button>
          <button 
            onClick={() => go('/admin/campers')} 
            className="nav-button rainbow-red"
          >
            Camper Management
          </button>
          <button 
            onClick={() => go('/admin/inventory')} 
            className="nav-button rainbow-yellow"
          >
            Inventory Management
          </button>
          <button 
            onClick={() => go('/admin')} 
            className="nav-button rainbow-purple"
          >
            Admin
          </button>
        </>
      )}
      <button 
        onClick={handleLogout} 
        className="nav-button logout"
      >
        Logout
      </button>
    </>
  );

  return (
    <nav className={`navigation-bar${isMobile ? ' navigation-bar--mobile' : ''}`}>
      <div className="nav-brand-row">
        <div className="nav-brand">
          <h1>Berwick Registration</h1>
        </div>
        {isMobile && (
          <button
            type="button"
            className="nav-menu-toggle"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? 'Close' : 'Menu'}
          </button>
        )}
      </div>
      <div className={`nav-buttons${isMobile ? (menuOpen ? ' is-open' : ' is-collapsed') : ''}`}>
        {navButtons}
      </div>
    </nav>
  );
};

export default Navigation;
